import { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

interface Participant {
  id: number | string;
  username?: string | null;
  full_name?: string | null;
  is_premium?: boolean;
  is_verified?: boolean;
  is_admin?: boolean;
  is_bot?: boolean;
  dc_id?: number | null;
  last_seen?: string;
  has_photo?: boolean;
  is_deleted?: boolean;
  is_scam?: boolean;
  is_fake?: boolean;
}

interface ParticipantNetworkGraphProps {
  participants: Participant[];
  height?: number;
  onNodeClick?: (node: any) => void;
}

export function ParticipantNetworkGraph({ 
  participants = [], 
  height = 600,
  onNodeClick 
}: ParticipantNetworkGraphProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const simulationRef = useRef<d3.Simulation<d3.SimulationNodeDatum, undefined> | null>(null);
  const [selectedNode, setSelectedNode] = useState<any>(null);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current || participants.length === 0) return;

    // Clear previous
    d3.select(svgRef.current).selectAll("*").remove();

    const width = containerRef.current.clientWidth;
    const containerHeight = height;

    // Build nodes
    const nodes: any[] = participants.map((p, idx) => {
      const userId = p.id || idx;
      const fullName = p.full_name || p.username || `User ${userId}`;
      const username = p.username || null;
      const isPremium = p.is_premium || false;
      const isVerified = p.is_verified || false;
      const isAdmin = p.is_admin || false;
      const isBot = p.is_bot || false;
      const dcId = p.dc_id || null;
      const hasPhoto = p.has_photo || false;
      const isDeleted = p.is_deleted || false;
      const isScam = p.is_scam || false;
      const isFake = p.is_fake || false;

      // Determine node color based on attributes
      let color = '#94a3b8'; // default gray
      if (isAdmin) color = '#ef4444'; // red for admin
      else if (isBot) color = '#a855f7'; // purple for bot
      else if (isVerified) color = '#2563eb'; // blue for verified
      else if (isPremium) color = '#f59e0b'; // yellow for premium
      else if (isScam) color = '#f97316'; // orange for scam
      else if (isFake) color = '#ec4899'; // pink for fake
      else if (isDeleted) color = '#6b7280'; // gray for deleted
      else color = '#3b82f6'; // blue for regular

      return {
        id: String(userId),
        label: fullName.length > 15 ? fullName.substring(0, 15) + '...' : fullName,
        fullLabel: fullName,
        username,
        isPremium,
        isVerified,
        isAdmin,
        isBot,
        dcId,
        hasPhoto,
        isDeleted,
        isScam,
        isFake,
        lastSeen: p.last_seen || 'Unknown',
        radius: isAdmin ? 12 : isVerified ? 10 : isPremium ? 9 : isBot ? 8 : 7,
        color,
        group: isAdmin ? 'admin' : isBot ? 'bot' : isVerified ? 'verified' : isPremium ? 'premium' : 'regular'
      };
    });

    // Build links based on relationships
    const links: any[] = [];
    
    // Link users by same DC
    const dcGroups: { [key: number]: string[] } = {};
    nodes.forEach(node => {
      if (node.dcId !== null && node.dcId !== undefined) {
        if (!dcGroups[node.dcId]) dcGroups[node.dcId] = [];
        dcGroups[node.dcId].push(node.id);
      }
    });
    
    Object.values(dcGroups).forEach(group => {
      if (group.length > 1) {
        for (let i = 0; i < group.length; i++) {
          for (let j = i + 1; j < group.length; j++) {
            links.push({
              source: group[i],
              target: group[j],
              type: 'dc',
              value: 1
            });
          }
        }
      }
    });

    // Link admins to all users (hierarchical)
    const adminNodes = nodes.filter(n => n.isAdmin);
    const regularNodes = nodes.filter(n => !n.isAdmin && !n.isBot);
    
    adminNodes.forEach(admin => {
      regularNodes.forEach(regular => {
        links.push({
          source: admin.id,
          target: regular.id,
          type: 'admin',
          value: 0.5
        });
      });
    });

    // Link users with same last seen status
    const lastSeenGroups: { [key: string]: string[] } = {};
    nodes.forEach(node => {
      const status = node.lastSeen || 'Unknown';
      if (!lastSeenGroups[status]) lastSeenGroups[status] = [];
      lastSeenGroups[status].push(node.id);
    });

    Object.entries(lastSeenGroups).forEach(([, group]) => {
      if (group.length > 1 && group.length <= 5) { // Only link small groups
        for (let i = 0; i < group.length; i++) {
          for (let j = i + 1; j < group.length; j++) {
            if (!links.find(l => 
              (l.source === group[i] && l.target === group[j]) ||
              (l.source === group[j] && l.target === group[i])
            )) {
              links.push({
                source: group[i],
                target: group[j],
                type: 'activity',
                value: 0.3
              });
            }
          }
        }
      }
    });

    // Create SVG
    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .attr("height", containerHeight);

    // Add zoom
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on("zoom", (event) => {
        container.attr("transform", event.transform);
      });

    svg.call(zoom as any);

    // Create container for zoomable content
    const container = svg.append("g");

    // Create force simulation
    const simulation = d3.forceSimulation(nodes as d3.SimulationNodeDatum[])
      .force("link", d3.forceLink(links).id((d: any) => d.id).distance((d: any) => {
        if (d.type === 'admin') return 150;
        if (d.type === 'dc') return 100;
        return 80;
      }))
      .force("charge", d3.forceManyBody().strength(-300))
      .force("center", d3.forceCenter(width / 2, containerHeight / 2))
      .force("collision", d3.forceCollide().radius((d: any) => d.radius + 5));

    // Create links
    const link = container.append("g")
      .selectAll("line")
      .data(links)
      .enter()
      .append("line")
      .attr("stroke", (d: any) => {
        if (d.type === 'admin') return '#ef4444';
        if (d.type === 'dc') return '#3b82f6';
        return '#94a3b8';
      })
      .attr("stroke-opacity", (d: any) => d.value || 0.3)
      .attr("stroke-width", (d: any) => (d.value || 0.5) * 2);

    // Create nodes
    const node = container.append("g")
      .selectAll("circle")
      .data(nodes)
      .enter()
      .append("circle")
      .attr("r", (d: any) => d.radius)
      .attr("fill", (d: any) => d.color)
      .attr("stroke", "#fff")
      .attr("stroke-width", 2)
      .style("cursor", "pointer")
      .call(d3.drag<SVGCircleElement, any>()
        .on("start", (event, d) => {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on("drag", (event, d) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on("end", (event, d) => {
          if (!event.active) simulation.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        }) as any
      )
      .on("click", (_, d) => {
        setSelectedNode(d);
        if (onNodeClick) onNodeClick(d);
      })
      .on("mouseover", function(event, d) {
        d3.select(this).attr("r", (d: any) => d.radius + 3);
        
        // Show tooltip
        const tooltip = container.append("g")
          .attr("class", "tooltip")
          .attr("transform", `translate(${event.x + 10},${event.y - 10})`);
        
        tooltip.append("rect")
          .attr("width", 200)
          .attr("height", 100)
          .attr("fill", "rgba(0, 0, 0, 0.8)")
          .attr("rx", 4);
        
        tooltip.append("text")
          .attr("x", 10)
          .attr("y", 20)
          .attr("fill", "white")
          .attr("font-size", "12px")
          .attr("font-weight", "bold")
          .text(d.fullLabel);
        
        if (d.username) {
          tooltip.append("text")
            .attr("x", 10)
            .attr("y", 40)
            .attr("fill", "#94a3b8")
            .attr("font-size", "10px")
            .text(`@${d.username}`);
        }
        
        tooltip.append("text")
          .attr("x", 10)
          .attr("y", 60)
          .attr("fill", "#94a3b8")
          .attr("font-size", "10px")
          .text(`ID: ${d.id}`);
        
        tooltip.append("text")
          .attr("x", 10)
          .attr("y", 80)
          .attr("fill", "#94a3b8")
          .attr("font-size", "10px")
          .text(`Last seen: ${d.lastSeen}`);
      })
      .on("mouseout", function() {
        d3.select(this).attr("r", (d: any) => d.radius);
        container.selectAll(".tooltip").remove();
      });

    // Add labels
    const labels = container.append("g")
      .selectAll("text")
      .data(nodes)
      .enter()
      .append("text")
      .text((d: any) => d.label)
      .attr("font-size", "10px")
      .attr("dx", (d: any) => d.radius + 5)
      .attr("dy", 4)
      .attr("fill", () => {
        // Use theme-aware colors
        const isDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
        return isDark ? "#e5e7eb" : "#1f2937";
      })
      .style("pointer-events", "none");

    // Update positions on simulation tick
    simulation.on("tick", () => {
      link
        .attr("x1", (d: any) => d.source.x)
        .attr("y1", (d: any) => d.source.y)
        .attr("x2", (d: any) => d.target.x)
        .attr("y2", (d: any) => d.target.y);

      node
        .attr("cx", (d: any) => d.x)
        .attr("cy", (d: any) => d.y);

      labels
        .attr("x", (d: any) => d.x)
        .attr("y", (d: any) => d.y);
    });

    simulationRef.current = simulation;

    // Cleanup
    return () => {
      if (simulationRef.current) {
        simulationRef.current.stop();
      }
    };
  }, [participants, height, onNodeClick]);

  return (
    <div className="w-full">
      <div ref={containerRef} className="w-full border rounded-lg bg-background">
        <svg ref={svgRef} className="w-full" style={{ height: `${height}px`, backgroundColor: 'transparent' }} />
      </div>
      {selectedNode && (
        <div className="mt-4 p-4 bg-muted rounded-lg">
          <h4 className="font-semibold mb-2">{selectedNode.fullLabel}</h4>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {selectedNode.username && <div>Username: @{selectedNode.username}</div>}
            <div>ID: {selectedNode.id}</div>
            {selectedNode.dcId !== null && <div>DC: {selectedNode.dcId}</div>}
            <div>Last seen: {selectedNode.lastSeen}</div>
            {selectedNode.isAdmin && <div className="text-red-600">Admin</div>}
            {selectedNode.isVerified && <div className="text-blue-600">Verified</div>}
            {selectedNode.isPremium && <div className="text-yellow-600">Premium</div>}
            {selectedNode.isBot && <div className="text-purple-600">Bot</div>}
          </div>
        </div>
      )}
    </div>
  );
}

